import type { OnboardingSegment } from '../store/onboarding-store';

export interface SegmentPersona {
  name: string;
  role: string;
  description: string;
  initials: string;
  color: string;
}

export interface SegmentDef {
  id: OnboardingSegment;
  title: string;
  emoji: string;
  subtitle: string;
  description: string;
  bgClass: string;
  borderClass: string;
  textClass: string;
  ringClass: string;
  examples: string[];
  features: { icon: string; label: string }[];
  tutorialCompany: string;
  tutorialScenario: string;
  personas: {
    admin: SegmentPersona;
    sales: SegmentPersona;
    approver: SegmentPersona;
  };
  demoModel: {
    name: string;
    description: string;
    variables: string[];
  };
  demoScenario: {
    client: string;
    product: string;
    context: string;
  };
}

export const SEGMENTS: SegmentDef[] = [
  {
    id: 'retail',
    title: 'Retail / Distribución',
    emoji: '🛒',
    subtitle: 'Venta de productos al consumidor o canal',
    description:
      'Gestiona precios de productos físicos, márgenes por categoría y descuentos escalonados por volumen.',
    bgClass: 'bg-blue-50 dark:bg-blue-950/20',
    borderClass: 'border-blue-200 dark:border-blue-700',
    textClass: 'text-blue-700 dark:text-blue-400',
    ringClass: 'ring-blue-500',
    examples: ['Supermercados', 'Distribuidoras', 'Mayoristas', 'Cadenas de tiendas'],
    features: [
      { icon: 'Package', label: 'Catálogo de productos con precios de lista' },
      { icon: 'Percent', label: 'Descuentos escalonados por volumen' },
      { icon: 'BarChart2', label: 'Márgenes por categoría y canal' },
      { icon: 'Users', label: 'Listas de precios diferenciadas por cliente' },
    ],
    tutorialCompany: 'SuperMart SA',
    tutorialScenario:
      'La distribuidora "Mercados del Norte" solicita 500 unidades de aceite comestible con descuento de canal.',
    personas: {
      admin: {
        name: 'Ana Torres',
        role: 'Administradora',
        description:
          'Configura los modelos de pricing por categoría y mantiene el catálogo de productos con márgenes objetivo.',
        initials: 'AT',
        color: 'bg-orange-500',
      },
      sales: {
        name: 'Pedro Gómez',
        role: 'Ejecutivo de Ventas',
        description:
          'Genera cotizaciones para distribuidores y cadenas, aplicando descuentos de canal y condiciones especiales.',
        initials: 'PG',
        color: 'bg-blue-500',
      },
      approver: {
        name: 'Rosa Medina',
        role: 'Gerente Comercial',
        description:
          'Aprueba cotizaciones con márgenes especiales o descuentos fuera de la política estándar de la empresa.',
        initials: 'RM',
        color: 'bg-green-500',
      },
    },
    demoModel: {
      name: 'Modelo Retail',
      description: 'Pricing de productos con costo, merma, margen objetivo y descuento de canal.',
      variables: [
        'COSTO_PRODUCTO',
        'MERMA_%',
        'COSTO_TOTAL',
        'MARGEN_%',
        'PRECIO_LISTA',
        'DESCUENTO_CANAL',
        'PRECIO_NETO',
      ],
    },
    demoScenario: {
      client: 'Mercados del Norte SA',
      product: 'Aceite Comestible Premium 1L',
      context:
        'Pedido de 500 unidades. Cliente aplica descuento de canal del 8% sobre precio de lista.',
    },
  },
  {
    id: 'b2b-importador',
    title: 'Importador B2B',
    emoji: '🚢',
    subtitle: 'Importación y distribución de productos',
    description:
      'Calcula precios CIF, aranceles, tipo de cambio y margen de importación en un solo flujo integrado.',
    bgClass: 'bg-teal-50 dark:bg-teal-950/20',
    borderClass: 'border-teal-200 dark:border-teal-700',
    textClass: 'text-teal-700 dark:text-teal-400',
    ringClass: 'ring-teal-500',
    examples: ['Importadores', 'Trading companies', 'Distribuidores de marca', 'Comercializadoras'],
    features: [
      { icon: 'Ship', label: 'Estructura de costo CIF / DDP completa' },
      { icon: 'DollarSign', label: 'Tipo de cambio configurable por cotización' },
      { icon: 'Receipt', label: 'Aranceles e impuestos de importación' },
      { icon: 'TrendingUp', label: 'Margen sobre costo landed final' },
    ],
    tutorialCompany: 'Bolivia Import SA',
    tutorialScenario:
      'La ferretería "Industrial Ltda." necesita 200 taladros importados con precio puesto en almacén del cliente.',
    personas: {
      admin: {
        name: 'Miguel Flores',
        role: 'Director Comercial',
        description:
          'Configura los modelos de importación con estructura de costos, aranceles y tipo de cambio de referencia.',
        initials: 'MF',
        color: 'bg-orange-500',
      },
      sales: {
        name: 'Sandra Castro',
        role: 'Ejecutiva Comercial',
        description:
          'Genera cotizaciones con precio final DDP, integrando todos los costos de importación en tiempo real.',
        initials: 'SC',
        color: 'bg-teal-500',
      },
      approver: {
        name: 'Roberto Paz',
        role: 'Gerente General',
        description:
          'Aprueba cotizaciones con márgenes ajustados por fluctuación de tipo de cambio o aranceles temporales.',
        initials: 'RP',
        color: 'bg-green-500',
      },
    },
    demoModel: {
      name: 'Modelo Importación',
      description: 'Estructura completa desde costo FOB hasta precio de venta DDP al cliente.',
      variables: [
        'COSTO_FOB',
        'TIPO_CAMBIO',
        'FLETE_CIF',
        'ARANCEL_%',
        'COSTO_LANDED',
        'MARGEN_%',
        'PRECIO_VENTA',
      ],
    },
    demoScenario: {
      client: 'Ferretería Industrial Ltda.',
      product: 'Taladro Industrial 20V (importado)',
      context: '200 unidades FOB China. Incluye flete, seguro, arancel del 15% y TC 6.96.',
    },
  },
  {
    id: 'tecnologia',
    title: 'Tecnología / Software',
    emoji: '💻',
    subtitle: 'Servicios y soluciones tecnológicas',
    description:
      'Cotiza proyectos de software, licencias, implementación y soporte con fórmulas en cascada avanzadas.',
    bgClass: 'bg-violet-50 dark:bg-violet-950/20',
    borderClass: 'border-violet-200 dark:border-violet-700',
    textClass: 'text-violet-700 dark:text-violet-400',
    ringClass: 'ring-violet-500',
    examples: ['Integradores de sistemas', 'ISVs', 'Consultoras TI', 'MSPs'],
    features: [
      { icon: 'Code2', label: 'Licencias + implementación en un modelo' },
      { icon: 'Clock', label: 'Horas de soporte y SLAs' },
      { icon: 'GitBranch', label: 'Cotizaciones multi-sección' },
      { icon: 'Shield', label: 'Garantías y renovaciones' },
    ],
    tutorialCompany: 'ACME Corporation',
    tutorialScenario:
      'Banco Nacional solicita 50 licencias de Microsoft 365 con implementación y soporte anual incluidos.',
    personas: {
      admin: {
        name: 'Carlos',
        role: 'Administrador',
        description:
          'Configura modelos de pricing, datos maestros y gestiona el equipo de ACME Corporation.',
        initials: 'CA',
        color: 'bg-orange-500',
      },
      sales: {
        name: 'María',
        role: 'Vendedora',
        description: 'Crea cotizaciones para clientes usando los modelos que Carlos configura.',
        initials: 'MV',
        color: 'bg-blue-500',
      },
      approver: {
        name: 'Juan',
        role: 'Aprobador',
        description: 'Revisa y aprueba (o rechaza) las cotizaciones que envían los vendedores.',
        initials: 'JA',
        color: 'bg-green-500',
      },
    },
    demoModel: {
      name: 'Modelo Software',
      description: 'Licencias, implementación y soporte con cálculo de margen en cascada.',
      variables: [
        'CANTIDAD',
        'COSTO_LICENCIA',
        'DESCUENTO_VOLUMEN',
        'COSTO_IMPLEMENTACION',
        'MARGEN_%',
        'PRECIO_FINAL',
      ],
    },
    demoScenario: {
      client: 'Banco Nacional',
      product: 'Microsoft 365 Business Premium (50 licencias)',
      context: 'Con implementación de $2.500 USD y soporte anual de $1.200 USD.',
    },
  },
  {
    id: 'servicios',
    title: 'Servicios Profesionales',
    emoji: '💼',
    subtitle: 'Consultoría, agencias y servicios',
    description:
      'Presupuesta servicios por hora, proyecto o retainer con estructura de perfiles, overhead y margen.',
    bgClass: 'bg-amber-50 dark:bg-amber-950/20',
    borderClass: 'border-amber-200 dark:border-amber-700',
    textClass: 'text-amber-700 dark:text-amber-400',
    ringClass: 'ring-amber-500',
    examples: ['Consultoras', 'Agencias digitales', 'Despachos legales', 'Firmas de auditoría'],
    features: [
      { icon: 'Users2', label: 'Tarifas diferenciadas por perfil' },
      { icon: 'Timer', label: 'Estimación de horas por fase' },
      { icon: 'Repeat', label: 'Retainers y contratos recurrentes' },
      { icon: 'FileText', label: 'Propuestas profesionales' },
    ],
    tutorialCompany: 'ConsultPro SA',
    tutorialScenario:
      'Cliente "Empresa ABC" necesita una consultoría de transformación digital de 3 meses con equipo mixto.',
    personas: {
      admin: {
        name: 'Laura Vega',
        role: 'Directora de Operaciones',
        description:
          'Define tarifas por perfil profesional, overhead del estudio y la estructura de costos de los proyectos.',
        initials: 'LV',
        color: 'bg-orange-500',
      },
      sales: {
        name: 'Daniel Mora',
        role: 'Gerente de Propuestas',
        description:
          'Arma propuestas comerciales con perfiles, horas estimadas por fase y valor total del proyecto.',
        initials: 'DM',
        color: 'bg-amber-500',
      },
      approver: {
        name: 'Patricia Ríos',
        role: 'Socia Directora',
        description:
          'Aprueba propuestas con tarifas especiales o márgenes fuera del estándar del estudio.',
        initials: 'PR',
        color: 'bg-green-500',
      },
    },
    demoModel: {
      name: 'Modelo Consultoría',
      description: 'Proyecto basado en perfiles, horas, overhead del estudio y margen de ganancia.',
      variables: [
        'HORAS_SENIOR',
        'TARIFA_SENIOR',
        'HORAS_JUNIOR',
        'TARIFA_JUNIOR',
        'OVERHEAD_%',
        'MARGEN_%',
        'PRECIO_PROYECTO',
      ],
    },
    demoScenario: {
      client: 'Empresa ABC',
      product: 'Consultoría Transformación Digital',
      context: '3 meses: 2 seniors (40h/mes) + 1 junior (60h/mes). Overhead 20%.',
    },
  },
];

export const SEGMENT_MAP = Object.fromEntries(SEGMENTS.map((s) => [s.id, s])) as Record<
  OnboardingSegment,
  SegmentDef
>;
